/**
 * SA-01 фаза 1 — ручной статус посева + закрытие сезона.
 * Пути — backend `api/v1/sowing_status.py`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Прогресс-статусы посева (ADR-046). Пар живёт отдельным полем, не в статусе.
 *
 * `written_off` — AK-513 (ADR-059 §D7): «Списано» из 4-значного
 * пользовательского канона ручной отметки (Запланировано · Растёт · Убрано ·
 * Списано). Не тождественно `failed` («Погиб») — то самостоятельное
 * историческое значение со своей подписью в `lib/sowings/stage.ts`.
 */
export type SowingStatusCode =
  | 'unplanned'
  | 'planned'
  | 'sown'
  | 'growing'
  | 'partial_harvest'
  | 'harvested'
  | 'failed'
  | 'reseeded'
  | 'closed'
  | 'finished'
  | 'written_off';

/**
 * Статусы, которыми закрывают сезон на экране «Закрытие сезона».
 *
 * AK-513 шаг 2 (ADR-059 §D7/§D8): было четыре (`closed`/`harvested`/`failed`/
 * `finished`), стало два. Нейтрального «Закрыт» канон не знает — такая строка
 * не отвечает, что случилось с урожаем, поэтому не попадает ни в уборочную
 * площадь, ни в списание. Честных исходов ровно два: **Убрано** (уборка была,
 * работой не отмечена) и **Списано** (урожая не будет). Сервер принимает
 * ровно этот список (`CloseSowingsIn.status`).
 */
export const CLOSING_STATUSES: SowingStatusCode[] = ['harvested', 'written_off'];

export interface SowingStatusLogRow {
  changed_at: string;
  old_status: string | null;
  old_status_label: string | null;
  status: string;
  status_label: string;
  reason: string | null;
  /** Дата события (сева/уборки/акта), не дата клика (ADR-059 §D4). */
  occurred_on?: string | null;
  who_user_id: string | null;
  who_name: string | null;
}

export interface OpenSowingRow {
  sowing_id: string;
  field_id: string;
  field_name: string;
  farm_id: string;
  farm_name: string | null;
  zone_name: string | null;
  occupancy_type: 'crop' | 'fallow';
  crop_name: string | null;
  fallow_name: string | null;
  status: string;
  status_label: string;
  area_ha: number | null;
}

export interface CloseSowingResult {
  sowing_id: string;
  field_name: string | null;
  ok: boolean;
  old_status: string | null;
  error: string | null;
}

export interface CloseSowingsResponse {
  closed: number;
  skipped: number;
  results: CloseSowingResult[];
}

export const sowingStatusKeys = {
  log: (sowingId: string) => ['sowing-status', 'log', sowingId] as const,
  openSowings: (seasonId: string | null, farmId: string | null) =>
    ['sowing-status', 'open', seasonId, farmId] as const,
};

/** PATCH /sowings/{id}/status — осознанная смена статуса с причиной и датой события. */
export function useSetSowingStatus(sowingId: string) {
  const qc = useQueryClient();
  return useMutation<
    { status: string; status_label: string; old_status: string | null },
    Error,
    {
      status: SowingStatusCode;
      reason?: string | null;
      /** Дата события (ADR-059 §D4) — обязательна на клиенте для Растёт/Убрано/Списано. */
      occurred_on?: string | null;
    }
  >({
    mutationFn: (body) => api.patch(`/api/v1/sowings/${sowingId}/status`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sowingStatusKeys.log(sowingId) });
      // статус виден в карточке поля и в списке закрытия сезона
      void qc.invalidateQueries({ queryKey: ['field-card'] });
      void qc.invalidateQueries({ queryKey: ['sowing-status', 'open'] });
    },
  });
}

/** GET /sowings/{id}/status-log — кто и когда менял статус. */
export function useSowingStatusLog(
  sowingId: string | null,
): UseQueryResult<SowingStatusLogRow[], Error> {
  return useQuery<SowingStatusLogRow[], Error>({
    queryKey: sowingStatusKeys.log(sowingId ?? ''),
    enabled: Boolean(sowingId),
    queryFn: () =>
      api.get<SowingStatusLogRow[]>(`/api/v1/sowings/${sowingId}/status-log`),
    staleTime: 15_000,
  });
}

/** GET /seasons/{id}/open-sowings — незавершённые посевы сезона. */
export function useOpenSowings(
  seasonId: string | null,
  farmId: string | null,
): UseQueryResult<OpenSowingRow[], Error> {
  return useQuery<OpenSowingRow[], Error>({
    queryKey: sowingStatusKeys.openSowings(seasonId, farmId),
    enabled: Boolean(seasonId),
    queryFn: () => {
      const qs = farmId ? `?farm_id=${encodeURIComponent(farmId)}` : '';
      return api.get<OpenSowingRow[]>(
        `/api/v1/seasons/${seasonId}/open-sowings${qs}`,
      );
    },
    staleTime: 15_000,
  });
}

/** POST /seasons/{id}/close-sowings — закрыть отмеченные посевы. */
export function useCloseSowings(seasonId: string | null) {
  const qc = useQueryClient();
  return useMutation<
    CloseSowingsResponse,
    Error,
    { sowing_ids: string[]; status: SowingStatusCode; reason?: string | null }
  >({
    mutationFn: (body) =>
      api.post<CloseSowingsResponse>(
        `/api/v1/seasons/${seasonId}/close-sowings`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sowing-status', 'open'] });
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}
